import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Radio, Music, Calendar, MapPin } from "lucide-react";
import { fetchPublicSetlist } from "@/lib/api";
import { formatDuration, formatTotal } from "@/lib/format";

export default function PublicSetlist() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPublicSetlist(slug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#121212] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-3">
            // 404
          </p>
          <h1 className="text-3xl font-bold mb-2">Setlist não encontrado</h1>
          <p className="text-zinc-500">
            Este link pode ter sido desativado ou nunca existiu.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#121212] text-zinc-500 flex items-center justify-center">
        Carregando...
      </div>
    );
  }

  const totalSec = data.songs.reduce(
    (acc, s) => acc + (s.duration_seconds || 0),
    0
  );

  return (
    <div className="min-h-screen bg-[#121212] text-white">
      {/* Public header */}
      <header className="border-b border-[#2D2D2D]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#00F0FF] flex items-center justify-center text-black">
            <Radio className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <p className="font-bold text-sm tracking-tight">SetlistPro</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              link público
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 printable">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-3">
          // repertório do show
        </p>
        <h1
          className="text-4xl sm:text-5xl font-bold tracking-tight mb-4"
          data-testid="public-setlist-name"
        >
          {data.name}
        </h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-zinc-400 mb-8 text-sm">
          {data.venue && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {data.venue}
            </span>
          )}
          {data.show_date && (
            <span className="inline-flex items-center gap-1.5 font-mono">
              <Calendar className="w-3.5 h-3.5" /> {data.show_date}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 font-mono text-[#00F0FF]">
            <Music className="w-3.5 h-3.5" /> {data.songs.length} faixas ·{" "}
            {formatTotal(totalSec)}
          </span>
        </div>

        {data.songs.length === 0 ? (
          <p className="text-zinc-500">Setlist ainda sem músicas.</p>
        ) : (
          <ol className="space-y-2" data-testid="public-songs">
            {data.songs.map((song, idx) => (
              <li
                key={song.id}
                data-testid={`public-song-${song.id}`}
                className="flex items-center gap-4 p-4 rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#00F0FF]/30 transition-colors"
              >
                <div className="font-mono text-xs text-zinc-500 w-8 text-center flex-shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-base sm:text-lg leading-tight">
                    {song.title}
                  </p>
                  <p className="text-sm text-zinc-500">{song.artist}</p>
                </div>
                <div className="hidden sm:flex flex-col items-end font-mono text-xs flex-shrink-0">
                  <span className="text-[#00F0FF]">{song.key || "—"}</span>
                  <span className="text-zinc-500">
                    {song.bpm || "—"} BPM ·{" "}
                    {formatDuration(song.duration_seconds)}
                  </span>
                </div>
                <div className="sm:hidden font-mono text-xs text-[#00F0FF] flex-shrink-0">
                  {formatDuration(song.duration_seconds)}
                </div>
              </li>
            ))}
          </ol>
        )}

        <footer className="mt-12 pt-6 border-t border-[#2D2D2D] text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-xs font-mono text-zinc-500 hover:text-[#00F0FF] transition-colors"
          >
            CRIE SEU SETLIST EM SETLISTPRO →
          </a>
        </footer>
      </main>
    </div>
  );
}
